import { BackIcon, DeleteIcon, EditIcon, ExclamationMarkOutlinedIcon, ExtendedMenuIcon } from '@synergy/theme-next';
import { BonusSettlementSupplierStatus, BonusSettlementType, CustomerBonusSettlement } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { MouseEvent, useState } from 'react';

import { bonusSettlementsSupplierDetailSelector } from './store/bonus-settlements-supplier-detail-selector';
import { useAppSelector } from '../../../store/use-appstate-selector';

interface BonusSettlementSupplierActionMenuProps {
    customer: CustomerBonusSettlement;
    onEditCustomer(): void;
    onReclaimCustomer(): void;
    onRevertReclaimCustomer(): void;
    onDeleteCustomer(): void;
}

export const BonusSettlementSupplierDetailTableActionMenu = (props: BonusSettlementSupplierActionMenuProps) => {
    const { customer, onEditCustomer, onReclaimCustomer, onRevertReclaimCustomer, onDeleteCustomer } = props;
    const bonusSettlementDetail = useAppSelector(bonusSettlementsSupplierDetailSelector);

    const [menuRef, setMenuRef] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(menuRef);

    const canDeleteCostumer =
        bonusSettlementDetail &&
        [
            BonusSettlementSupplierStatus.Open,
            BonusSettlementSupplierStatus.Reclaimed,
            BonusSettlementSupplierStatus.PartiallySent,
        ].includes(bonusSettlementDetail.status);

    const handleMenuClick = (e: MouseEvent<HTMLButtonElement>): void => {
        setMenuRef(e.currentTarget);
        e.stopPropagation();
    };

    return (
        <>
            <IconButton color="dark" size="medium" onClick={handleMenuClick}>
                <ExtendedMenuIcon />
            </IconButton>
            <Menu
                anchorEl={menuRef}
                open={isMenuOpen}
                onClose={(event: React.MouseEvent<HTMLElement>) => {
                    event.stopPropagation();
                    setMenuRef(null);
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {bonusSettlementDetail?.type === BonusSettlementType.Reclaim && customer.isReclaimed === false && (
                    <MenuItem
                        onClick={e => {
                            e.stopPropagation();
                            onReclaimCustomer();
                            setMenuRef(null);
                        }}
                    >
                        <ExclamationMarkOutlinedIcon sx={{ mr: 1 }} />
                        <FormattedMessage id="app.bonusSettlementsSupplierOverview.action.reclaimCustomer" />
                    </MenuItem>
                )}
                {bonusSettlementDetail?.type === BonusSettlementType.Reclaim && customer.isReclaimed === true && (
                    <MenuItem
                        onClick={e => {
                            e.stopPropagation();
                            onRevertReclaimCustomer();
                            setMenuRef(null);
                        }}
                    >
                        <BackIcon sx={{ mr: 1 }} />
                        <FormattedMessage id="app.bonusSettlementsSupplierOverview.action.revertReclaim" />
                    </MenuItem>
                )}
                {customer?.isAddedManually && canDeleteCostumer && (
                    <MenuItem
                        onClick={e => {
                            e.stopPropagation();
                            onDeleteCustomer();
                            setMenuRef(null);
                        }}
                    >
                        <DeleteIcon sx={{ mr: 1 }} />
                        <FormattedMessage id="app.bonusSettlements.supplierDetail.detail.deleteSupplier" />
                    </MenuItem>
                )}
                <MenuItem
                    onClick={e => {
                        e.stopPropagation();
                        onEditCustomer();
                        setMenuRef(null);
                    }}
                >
                    <EditIcon sx={{ mr: 1 }} />
                    <FormattedMessage id="app.bonusSettlements.supplierDetail.edit" />
                </MenuItem>
            </Menu>
        </>
    );
};
